<template>
	<div class="cassie-vertical-md pt-4">
		<FilterBar
			:search-query.sync="consentCollectionPreferencePageSearchQuery"
			search-query-label="Preference Page Name"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			@persistSearchQuery="changeConsentCollectionPreferencePageSearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
			@clearFilters="clearFilters"
		>
			<template #after-filters>
				<Dropdown
					v-model="consentCollectionPreferencePageAuthType"
					:items="authTypeFilter"
					custom-sort
					label="Authentication Type"
					@input="changeConsentCollectionPreferencePageAuthType($event)"
				/>
				<Dropdown
					v-model="consentCollectionPreferencePageStatusType"
					:items="statusFilterItems"
					custom-sort
					label="Status"
					@input="changeConsentCollectionPreferencePageStatusType($event)"
				/>
			</template>
			<template #action>
				<PrimaryActionButton
					v-if="userFullPermissions"
					@click="$router.push({
						name: MANAGE_CONSENT_COLLECTION_PREFERENCE_PAGE,
						params: {
							userFullPermissions: userFullPermissions
						}
					})"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Preference Pages
			</template>
			<template #body>
				<DataTable
					sort-desc
					sort-by="id"
					:headers="tableHeaders"
					:items="filteredPreferencePages"
					@click:row="onEditClick"
				>
					<template #item.preferencePageName="{ item }">
						{{ item.preferencePageName }}
						<v-chip
							v-if="item.status !== COMPLETE_STATUS"
							x-small
							class="ml-2 prussian-blue white--text"
						>
							In Progress
						</v-chip>
					</template>
					<template #item.action="{ item }">
						<v-menu offset-y>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId)"
									@click="onEditClick(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Edit
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-else
									@click="onEditClick(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											View
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="item.status === 5"
									@click.stop.prevent="onViewEmbedClick(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											View Embed Code
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="shouldShowPreview(item) && item.status === 5"
									@click.stop="onPreviewClick(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Preview Link
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId)"
									@click.stop.prevent="preferencePageToRemove = item"
								>
									<v-list-item-content>
										<v-list-item-title>
											Delete
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<ConfirmDeleteModal
			v-if="preferencePageToRemove"
			:entity-name="preferencePageToRemove.name"
			entity-type="Preference Page"
			@close="preferencePageToRemove = null"
			@delete="deletePreferencePage"
		/>
		<ViewPrefPageLinkModal
			v-if="prefPageToView"
			v-bind="prefPageToView"
			:assigned-language-items="assignedLanguagesItems"
			@close="prefPageToView = null"
		/>
	</div>
</template>

<script>
import ViewPrefPageLinkModal from './view-prefpage-link-modal.vue'
import FilterBar from '../../../../../../shared/components/filter-bar.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'

import Dropdown from '../../../../../../shared/components/dropdown.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId } from '../../../../../../shared/state/brands.js'
import {
	changeConsentCollectionPreferencePageSearchQuery,
	consentCollectionPreferencePageSearchQuery,
	changeConsentCollectionPreferencePageAuthType,
	consentCollectionPreferencePageAuthType,
	changeConsentCollectionPreferencePageStatusType,
	consentCollectionPreferencePageStatusType
} from '../../../../../../shared/state/consent-collection-preference-pages.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import {
	MANAGE_CONSENT_COLLECTION_PREFERENCE_PAGE
} from '../../../../router/route-names.js'
import { deletePreferencePage } from '../../../../../../shared/utils/api/preference-pages.js'
import { encryptParameters } from '../../../../../../shared/utils/api/admin-portal/links/links.js'
import previewLinkMixin from '../../../../../../shared/mixins/preview-link.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'

export default {
	components: {
		SectionCard,
		FilterBar,
		DataTable,
		PrimaryActionButton,
		Dropdown,
		ConfirmDeleteModal,
		ViewPrefPageLinkModal
	},
	mixins: [previewLinkMixin],
	props: {
		preferencePages: Array,
		userFullPermissions: Boolean,
		userHasBrand: Function,
		assignedLanguages: Array
	},
	setup () {
		return {
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			changeConsentCollectionPreferencePageSearchQuery,
			consentCollectionPreferencePageSearchQuery,
			changeConsentCollectionPreferencePageAuthType,
			consentCollectionPreferencePageAuthType,
			changeConsentCollectionPreferencePageStatusType,
			consentCollectionPreferencePageStatusType,
			MANAGE_CONSENT_COLLECTION_PREFERENCE_PAGE
		}
	},
	data () {
		const COMPLETE_STATUS = 5
		return {
			preferencePageToRemove: null,
			prefPageToView: null,
			COMPLETE_STATUS
		}
	},
	computed: {
		tableHeaders () {
			return [
				{ text: 'ID', value: 'id', width: '10%' },
				{ text: useLabels('Brand'), value: 'brandName', width: '22%' },
				{ text: 'Preference Page Name', value: 'preferencePageName', width: '23%' },
				{ text: 'Authentication Type', value: 'authType', width: '23%' },
				{ text: 'Created', value: 'createdDate', width: '22%' },
				{ text: 'Action', value: 'action', sortable: false, width: '80px', align: 'center' }
			]
		},
		authTypeFilter () {
			return [
				{ value: null, text: 'View All' },
				{ value: 2, text: 'Confirm Value' },
				{ value: 1, text: 'Direct' },
				{ value: 3, text: 'Prove Email' },
				{ value: 5, text: 'Provide and Prove Email' },
				{ value: 4, text: 'Prove Value' }
			]
		},
		statusFilterItems () {
			return [
				{ value: null, text: 'View All' },
				{ value: true, text: 'Complete' },
				{ value: false, text: 'In Progress' }
			]
		},
		filteredPreferencePages () {
			return this.preferencePages.filter(({ preferencePageName, brandId, authTypeId, complete }) => {
				let check = true
				if (consentCollectionPreferencePageSearchQuery.value) check = preferencePageName.toLowerCase().includes(consentCollectionPreferencePageSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && selectedAdminPortalBrandId.value === brandId
				if (consentCollectionPreferencePageAuthType.value !== null) check = check && consentCollectionPreferencePageAuthType.value === authTypeId
				if (consentCollectionPreferencePageStatusType.value !== null) check = check && consentCollectionPreferencePageStatusType.value === complete
				return check
			})
		},
		assignedLanguagesItems () {
			return this.assignedLanguages.map(language => ({
				...language,
				languageCode: language.languageID === 0 ? 'default' : language.languageCode,
				value: language.languageID,
				text: language.languageName
			}))
		}
	},
	methods: {
		onEditClick (preferencePageToEdit) {
			this.$router.push({
				name: MANAGE_CONSENT_COLLECTION_PREFERENCE_PAGE,
				params: {
					userFullPermissions: this.userFullPermissions && this.userHasBrand(preferencePageToEdit.brandId)
				},
				query: {
					id: preferencePageToEdit.id
				}
			})
		},
		shouldShowPreview (prefPage) {
			return !(prefPage.linkFields?.find(
				field => !field.previewValue || field.previewValue === ''
			) ?? null)
		},
		async onPreviewClick (prefPage) {
			const tempString = this.getLinkHref(prefPage)
			await this.previewLink(tempString, prefPage)
		},
		async getEncryptedData (encryptedParams, prefPage) {
			return await encryptParameters({
				encryptionKeyId: prefPage.encryptionKeyId,
				linkParams: encryptedParams
			})
		},
		getLinkHref (prefPage) {
			return new DOMParser()
				.parseFromString(prefPage.embedLink, 'text/html')
				.getElementsByTagName('a')[0].href
		},
		onViewEmbedClick (prefPage) {
			this.prefPageToView = prefPage
		},
		async deletePreferencePage () {
			await deletePreferencePage(this.preferencePageToRemove.emailLinkId)
			this.preferencePageToRemove = null
			this.$emit('load')
			showSnackbar('Preference Page has been removed.')
		},
		clearFilters () {
			changeConsentCollectionPreferencePageAuthType(null)
			changeConsentCollectionPreferencePageStatusType(null)
		}
	}
}
</script>
